import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Dictionary, en } from "./en";
import { es } from "./es";

export type Lang = "en" | "es";

const STORAGE_KEY = "lang";
const dictionaries: Record<Lang, Dictionary> = { en, es };

interface LanguageContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  // The active dictionary
  t: Dictionary;
}

const LanguageContext = createContext<LanguageContextValue>({
  lang: "en",
  setLang: () => undefined,
  t: en,
});

function isLang(value: unknown): value is Lang {
  return value === "en" || value === "es";
}

function detectInitialLang(): Lang {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isLang(stored)) return stored;
  } catch {
    // Storage can be blocked (private mode, disabled cookies): fall through to the browser language
  }
  const browserLang = typeof navigator !== "undefined" ? navigator.language : "";
  return browserLang && browserLang.toLowerCase().startsWith("es") ? "es" : "en";
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(detectInitialLang);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Not persisted: the choice still applies for this visit
    }
  }, []);

  const value = useMemo(() => ({ lang, setLang, t: dictionaries[lang] }), [lang, setLang]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  return useContext(LanguageContext);
}
